import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { companyInfo } from '../data';
import { imageUrl } from '../lib/images';

export default function Badevaerelser() {
  const location = useLocation();
  const isEn = location.pathname.startsWith('/en');
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq((prev) => (prev === index ? null : index));
  };

  const content = {
    da: {
      metaTitle: 'Nyt badeværelse Billund – Renovering til fast pris | Billund VVS',
      metaDesc:
        'Drømmer du om nyt badeværelse i Billund, Grindsted eller omegn? Vi klarer hele badeværelsesrenoveringen med VVS, fliser, sanitet og totalentreprise til fast pris.',
      canonical: 'https://billundvvs.netlify.app/vvs/badevaerelser',
      heroH1: 'Badeværelser',
      heroH2: 'Nyt badeværelse – renovering og totalløsninger',
      heroLead:
        'Vi hjælper med renovering, modernisering og etablering af nye badeværelser. Vi sørger for professionel rådgivning, funktionelle løsninger og kvalitetsarbejde fra start til slut.',
      features: [
        {
          icon: 'fa-solid fa-shower',
          title: 'Totalrenovering',
          desc: 'Komplet fornyelse af badeværelset fra gulv til loft med faste samarbejdspartnere.',
        },
        {
          icon: 'fa-solid fa-wrench',
          title: 'VVS & sanitet',
          desc: 'Montering af armaturer, væghængte toiletter, brusenicher og afløb.',
        },
        {
          icon: 'fa-solid fa-temperature-arrow-up',
          title: 'Vandbåren gulvvarme',
          desc: 'Komfortabel gulvvarme med korrekt styring og optimal energieffektivitet.',
        },
        {
          icon: 'fa-solid fa-certificate',
          title: 'Autoriseret garanti',
          desc: 'Korrekt vådrumssikring og installation udført efter bygningsreglementet med TEKNIQ Garanti.',
        },
      ],
      galleryTitle: 'Eksempler på badeværelsesprojekter',
      galleryItems: [
        {
          id: 'foer',
          image: '/_assets/bad-foer-aeldre-badevaerelse.jpg',
          alt: 'Ældre udtjent badeværelse før renovering',
          badge: 'FØR',
          badgeBg: 'bg-[#4b5563]',
          caption: 'Ældre badeværelse før renovering',
        },
        {
          id: 'efter',
          image: '/_assets/bad-efter-nyt-luxusbad.jpg',
          alt: 'Nyt moderne badeværelse med store klinker og unidrain',
          badge: 'EFTER',
          badgeBg: 'bg-[#d11218]',
          caption: 'Moderne badeværelse med luksusfliser & regnbruser',
        },
        {
          id: 'bruseniche',
          image: '/_assets/bad-bruseniche-fliser.jpg',
          alt: 'Walk-in bruseniche med linjeafløb og indbygget vægniche',
          caption: 'Walk-in bruseniche med integreret afløb',
        },
        {
          id: 'sanitet',
          image: '/_assets/bad-moebel-armatur-detail.jpg',
          alt: 'Svævende badmøbel med eksklusivt armatur og spejl',
          caption: 'Eksklusivt badmøbel og moderne armaturer',
        },
      ],
      ctaButton: 'Få et uforpligtende tilbud',
      commercialTitle: 'Badeværelser til både private og erhverv',
      commercialLead:
        'Vi rådgiver om den bedste løsning til din bolig, sommerhus eller virksomhed. Vi hjælper med hele processen – fra første opmåling og materialevalg til færdig aflevering.',
      phoneCta: 'Ring 75 33 12 76',
      contactCta: 'Skriv til os',
      processTitle: 'Sådan forløber en badeværelsesrenovering',
      processSteps: [
        {
          step: '01',
          title: 'Gratis besigtigelse & rådgivning',
          desc: 'Vi mødes i din bolig, måler op, drøfter ønsker til indretning, sanitet og fliser.',
        },
        {
          step: '02',
          title: 'Fast tilbud & tidsplan',
          desc: 'Du modtager et gennemskueligt tilbud med fast pris og en klar køreplan for hele forløbet.',
        },
        {
          step: '03',
          title: 'Nedbrydning & nye installationer',
          desc: 'Det gamle rum demonteres, og vi etablerer nye vandrør, afløb, gulvvarme og el.',
        },
        {
          step: '04',
          title: 'Vådrumssikring & flisearbejde',
          desc: 'Godkendt vådrumsmembran påføres, og dygtige murere lægger klinker præcist efter dine ønsker.',
        },
        {
          step: '05',
          title: 'Montering, aflevering & garanti',
          desc: 'Sanitet, møbler og armaturer monteres. Vi afleverer et rengjort rum med fuld dokumentation.',
        },
      ],
      valuesTitle: 'Hvorfor vælge Billund Varme & Sanitet?',
      values: [
        {
          icon: 'fa-solid fa-users-gear',
          title: 'Én fast totalentreprenør',
          desc: 'Du har én kontaktperson gennem hele renoveringen – vi koordinerer VVS, murer, tømrer og el.',
        },
        {
          icon: 'fa-solid fa-shield-halved',
          title: 'TEKNIQ Garantiordning',
          desc: 'Alt autoriseret VVS-arbejde udføres fagmæssigt korrekt og er dækket af TEKNIQ Garanti.',
        },
        {
          icon: 'fa-solid fa-file-circle-check',
          title: 'Bygningsreglement og vådrumssikring',
          desc: 'Vi garanterer 100 % tætte vådrum efter SBI-anvisningerne med komplet fotodokumentation.',
        },
        {
          icon: 'fa-solid fa-clock-rotate-left',
          title: 'Lokal forankring siden 1963',
          desc: 'Over 60 års solid håndværkserfaring i Billund, Grindsted, Give, Vejle og omegn.',
        },
      ],
      faqTitle: 'Ofte stillede spørgsmål om nyt badeværelse',
      faqs: [
        {
          q: 'Hvad koster et nyt badeværelse typisk?',
          a: 'Prisen på en badeværelsesrenovering varierer alt efter størrelse, materialevalg og om rør og afløb skal flyttes. En mindre modernisering koster typisk fra 60.000–90.000 kr., mens en komplet totalrenovering med nye rør, gulvvarme, klinker og sanitet typisk ligger mellem 120.000 og 220.000 kr. inkl. moms. Vi udarbejder altid et uforpligtende tilbud med fast pris.',
        },
        {
          q: 'Hvor lang tid tager det at renovere et badeværelse?',
          a: 'En komplet totalrenovering tager sædvanligvis mellem 3 og 5 uger afhængigt af rummets størrelse og tørretider for støbning og vådrumssikring. Vi aftaler en fast tidsplan inden opstart, så du præcis ved, hvornår rummet står klar.',
        },
        {
          q: 'Kan toilettet eller brusenichen flyttes til en anden væg?',
          a: 'Ja, i langt de fleste tilfælde kan vi flytte både toilet, vask og bruseniche. Det kræver omføring af afløb og vandrør i gulv eller væg. Vi undersøger de byggetekniske muligheder under vores uforpligtende besigtigelse.',
        },
        {
          q: 'Står I for hele koordineringen med andre håndværkere?',
          a: 'Ja! Vi fungerer ofte som totalentreprenør, hvor vi står for hele koordineringen med faste, lokale murere, elektrikere, malere og tømrere. Du slipper for besværet med at styre flere håndværkere og har kun ét kontaktpunkt.',
        },
        {
          q: 'Er der garanti på vådrumssikring og installationer?',
          a: 'Ja. Alt vores arbejde overholder gældende bygningsreglement (BR18) og vådrumsnormer. Som autoriseret VVS-installatør er vores arbejde desuden dækket af TEKNIQ Garantiordning, hvilket sikrer dig økonomisk tryghed.',
        },
      ],
    },
    en: {
      metaTitle: 'New Bathroom Billund – Renovation at Fixed Price | Billund VVS',
      metaDesc:
        'Dreaming of a new bathroom in Billund, Grindsted or surrounding areas? Complete bathroom renovation with plumbing, tiling, sanitation and turnkey project management.',
      canonical: 'https://billundvvs.netlify.app/en/plumbing/bathrooms',
      heroH1: 'Bathrooms',
      heroH2: 'New bathroom – renovation and turnkey solutions',
      heroLead:
        'We help with complete renovation, modernization, and creation of new bathrooms. We provide professional guidance, functional layouts, and top craftsmanship from start to finish.',
      features: [
        {
          icon: 'fa-solid fa-shower',
          title: 'Complete renovation',
          desc: 'Full renewal of your bathroom from floor to ceiling with trusted local trade partners.',
        },
        {
          icon: 'fa-solid fa-wrench',
          title: 'Plumbing & sanitary ware',
          desc: 'Mounting of taps, wall-hung toilets, walk-in showers, and reliable drainage systems.',
        },
        {
          icon: 'fa-solid fa-temperature-arrow-up',
          title: 'Underfloor heating',
          desc: 'Hydronic floor heating with precision temperature control and high energy efficiency.',
        },
        {
          icon: 'fa-solid fa-certificate',
          title: 'Certified warranty',
          desc: 'Approved wet room waterproofing according to Danish building code covered by TEKNIQ Guarantee.',
        },
      ],
      galleryTitle: 'Examples of bathroom projects',
      galleryItems: [
        {
          id: 'foer',
          image: '/_assets/bad-foer-aeldre-badevaerelse.jpg',
          alt: 'Old outdated bathroom before renovation',
          badge: 'BEFORE',
          badgeBg: 'bg-[#5b6471]',
          caption: 'Older bathroom before renovation',
        },
        {
          id: 'efter',
          image: '/_assets/bad-efter-nyt-luxusbad.jpg',
          alt: 'New modern bathroom with large tiles and unidrain',
          badge: 'AFTER',
          badgeBg: 'bg-[#d11218]',
          caption: 'Modern bathroom with luxury tiles & rain shower',
        },
        {
          id: 'bruseniche',
          image: '/_assets/bad-bruseniche-fliser.jpg',
          alt: 'Walk-in shower with linear drain and built-in wall niche',
          caption: 'Walk-in shower with integrated drainage',
        },
        {
          id: 'sanitet',
          image: '/_assets/bad-moebel-armatur-detail.jpg',
          alt: 'Floating vanity unit with premium tapware and mirror',
          caption: 'Custom vanity and premium fixtures',
        },
      ],
      ctaButton: 'Get a non-binding quote',
      commercialTitle: 'Bathrooms for private homes and commercial properties',
      commercialLead:
        'We advise on the optimal layout for your house, holiday home, or commercial facility. We assist through the whole journey – from initial measuring to final handover.',
      phoneCta: 'Call +45 75 33 12 76',
      contactCta: 'Write to us',
      processTitle: 'How a bathroom renovation works',
      processSteps: [
        {
          step: '01',
          title: 'Free on-site consultation',
          desc: 'We inspect your home, measure the room, and discuss layout ideas, fixtures and tiles.',
        },
        {
          step: '02',
          title: 'Fixed price quote & schedule',
          desc: 'You receive a transparent quote with a fixed price and an agreed timeline for the work.',
        },
        {
          step: '03',
          title: 'Demolition & new installations',
          desc: 'The old room is stripped down, and we install new water supply, drain pipes, and underfloor heating.',
        },
        {
          step: '04',
          title: 'Waterproofing & tiling',
          desc: 'Approved waterproofing membrane is applied, followed by precision tiling by skilled craftsmen.',
        },
        {
          step: '05',
          title: 'Fitting, handover & warranty',
          desc: 'Toilets, sinks and faucets are mounted. We hand over a clean room with full warranty documentation.',
        },
      ],
      valuesTitle: 'Why choose Billund Varme & Sanitet?',
      values: [
        {
          icon: 'fa-solid fa-users-gear',
          title: 'One dedicated contractor',
          desc: 'Single point of contact – we manage all trades including plumbing, tiling, electrical and carpentry.',
        },
        {
          icon: 'fa-solid fa-shield-halved',
          title: 'TEKNIQ Guarantee scheme',
          desc: 'All authorized plumbing work is carried out professionally and backed by the TEKNIQ warranty.',
        },
        {
          icon: 'fa-solid fa-file-circle-check',
          title: 'Building code compliance',
          desc: '100% compliant waterproof wet rooms with detailed documentation for insurance purposes.',
        },
        {
          icon: 'fa-solid fa-clock-rotate-left',
          title: 'Local experience since 1963',
          desc: 'Over 60 years of proven craftsmanship in Billund, Grindsted, Give, Vejle and nearby towns.',
        },
      ],
      faqTitle: 'Frequently asked questions about new bathrooms',
      faqs: [
        {
          q: 'What does a new bathroom typically cost?',
          a: 'Costs depend on room dimensions, fixture choices, and whether pipe layouts are altered. Minor upgrades start from 60,000–90,000 DKK, while full turnkey renovations with new plumbing, underfloor heating, tiling, and sanitary ware range from 120,000 to 220,000 DKK incl. VAT.',
        },
        {
          q: 'How long does a full bathroom renovation take?',
          a: 'A complete renovation usually takes 3 to 5 weeks depending on drying times for concrete and waterproof membranes. We agree on a fixed schedule before starting.',
        },
        {
          q: 'Can the toilet or shower be relocated to a different wall?',
          a: 'Yes, in almost all cases. It requires relocating drainage and supply lines in the floor or walls. We evaluate the structural possibilities during our initial inspection.',
        },
        {
          q: 'Do you manage all other trades involved?',
          a: 'Yes! We frequently act as the main turnkey contractor, coordinating with trusted local bricklayers, electricians, and carpenters. You only deal with one responsible point of contact.',
        },
        {
          q: 'Is there a warranty on waterproofing and plumbing?',
          a: 'Yes, our work strictly conforms to Danish building regulations (BR18) and is fully covered under the official TEKNIQ Guarantee scheme.',
        },
      ],
    },
  };

  const t = isEn ? content.en : content.da;

  return (
    <div className="bg-white text-gray-900 font-sans">
      <Helmet>
        <title>{t.metaTitle}</title>
        <meta name="description" content={t.metaDesc} />
        <link rel="canonical" href={t.canonical} />
        <meta property="og:title" content={t.metaTitle} />
        <meta property="og:description" content={t.metaDesc} />
        <meta
          property="og:image"
          content="https://assets.auxodev.dk/assets/7f5366e1-5aaf-452f-9c72-7b11437bc331/bad-hero-moderne-renovering.jpg"
        />
      </Helmet>

      {/* Hero Section with right side cover image and smooth gradient flow */}
      <section className="relative overflow-hidden bg-white border-b border-gray-100 py-10 sm:py-16 lg:py-20 min-h-[460px] lg:min-h-[520px] flex items-center">
        {/* Right side cover image with smooth gradient flow */}
        <div className="hidden lg:block absolute inset-y-0 right-0 w-1/2 xl:w-[50%] 2xl:w-[52%] h-full pointer-events-none select-none overflow-hidden">
          <img
            src={imageUrl('/_assets/bad-hero-moderne-renovering.jpg', 'width=1400,quality=90,format=auto')}
            alt={isEn ? 'Modern renovated Scandinavian bathroom' : 'Nyt moderne renoveret badeværelse i lyst dagslys'}
            className="w-full h-full object-cover object-center"
            loading="eager"
            fetchPriority="high"
          />
          {/* Multi-stage horizontal gradient flow from white to transparent */}
          <div className="absolute inset-y-0 left-0 w-44 bg-gradient-to-r from-white via-white/80 to-transparent" />
          <div className="absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-white/20 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white/20 to-transparent" />

          {/* Subtle certification badge in bottom corner */}
          <div className="absolute bottom-5 right-6 pointer-events-auto bg-white/90 backdrop-blur-xs px-3.5 py-1.5 rounded-full shadow-xs border border-white/60 flex items-center gap-2 text-xs font-semibold text-[#09263f]">
            <i className="fa-solid fa-certificate text-[#d11218]" aria-hidden="true"></i>
            <span>TEKNIQ Autoriseret Installatør 2026–2028</span>
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-xl lg:max-w-xl">
            <h1 className="text-4xl sm:text-5xl lg:text-[3.25rem] font-bold text-[#09263f] tracking-tight leading-[1.1]">
              {t.heroH1}
            </h1>
            <h2 className="text-xl sm:text-2xl font-bold text-[#09263f] leading-snug mt-2">
              {t.heroH2}
            </h2>
            <p className="text-gray-700 text-base sm:text-lg leading-relaxed pt-3">
              {t.heroLead}
            </p>

            {/* Action Buttons */}
            <div className="pt-6 flex flex-wrap gap-4 items-center">
              <Link
                to={isEn ? '/en/get-a-quote' : '/tilbud'}
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#d11218] hover:bg-[#b50e14] text-white font-semibold text-base rounded-md shadow-sm transition-colors"
              >
                <span>{t.ctaButton}</span>
                <i className="fa-solid fa-arrow-right text-sm"></i>
              </Link>
              <a
                href={`tel:${companyInfo.phoneRaw}`}
                className="inline-flex items-center gap-2 px-5 py-3.5 border border-gray-300 hover:border-[#09263f] bg-white text-[#09263f] font-semibold text-base rounded-md transition-colors"
              >
                <i className="fa-solid fa-phone text-sm text-[#09263f]"></i>
                <span>{companyInfo.phone}</span>
              </a>
            </div>

            {/* Trust Badges */}
            <div className="pt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs sm:text-sm text-gray-500">
              <span className="inline-flex items-center gap-1.5 font-medium text-gray-700">
                <i className="fa-solid fa-shield-halved text-emerald-600"></i>
                TEKNIQ Garantiordning
              </span>
              <span className="inline-flex items-center gap-1.5 font-medium text-gray-700">
                <i className="fa-solid fa-award text-amber-600"></i>
                Autoriseret VVS & Vådrumssikring
              </span>
            </div>

            {/* Mobile & Tablet fallback image */}
            <div className="mt-8 lg:hidden">
              <div className="relative aspect-[16/10] w-full overflow-hidden rounded-[8px] bg-gray-50 shadow-sm border border-gray-200">
                <img
                  src={imageUrl('/_assets/bad-hero-moderne-renovering.jpg', 'width=1000,quality=85,format=auto')}
                  alt={isEn ? 'Modern renovated Scandinavian bathroom' : 'Nyt moderne badeværelse'}
                  className="w-full h-full object-cover"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Strip / Fordelsbjælke (4 Columns with Navy Circle Badges) */}
      <section className="bg-white border-b border-gray-200 py-8 sm:py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {t.features.map((feature, idx) => (
              <div key={idx} className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#09263f] text-white flex items-center justify-center shrink-0 shadow-xs">
                  <i className={`${feature.icon} text-lg`}></i>
                </div>
                <div>
                  <h3 className="text-base font-bold text-[#09263f] leading-snug">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-snug mt-1">
                    {feature.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Examples / Showcase Gallery (Eksempler på badeværelsesprojekter) */}
      <section className="bg-[#edf2f7] py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#09263f] mb-8 sm:mb-10">
            {t.galleryTitle}
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 items-start">
            {t.galleryItems.map((item) => (
              <div key={item.id} className="flex flex-col">
                {/* Image Container with high visual fidelity */}
                <div className="overflow-hidden rounded-lg shadow-sm border border-gray-200/80 bg-white aspect-[3/4] sm:aspect-[4/5] group">
                  <img
                    src={imageUrl(item.image, 'width=700,quality=85,format=auto')}
                    alt={item.alt}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>

                {/* Badge if present (FØR / EFTER) */}
                {item.badge && (
                  <div
                    className={`w-full ${item.badgeBg} text-white text-center font-bold uppercase tracking-wider py-2 rounded-md text-sm mt-3 shadow-xs`}
                  >
                    {item.badge}
                  </div>
                )}

                {/* Caption Text */}
                <p
                  className={`text-center font-medium text-gray-800 text-sm sm:text-base leading-snug ${
                    item.badge ? 'mt-2' : 'mt-3'
                  }`}
                >
                  {item.caption}
                </p>
              </div>
            ))}
          </div>

          {/* Centered Primary CTA Button */}
          <div className="mt-10 sm:mt-12 text-center">
            <Link
              to={isEn ? '/en/get-a-quote' : '/tilbud'}
              className="inline-flex items-center justify-center gap-2.5 px-8 py-3.5 bg-[#d11218] hover:bg-[#b50e14] text-white font-semibold text-base sm:text-lg rounded-md shadow-sm transition-all transform hover:-translate-y-0.5"
            >
              <span>{t.ctaButton}</span>
              <i className="fa-solid fa-arrow-right text-sm"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Commercial & Private Section */}
      <section className="bg-white py-12 sm:py-16 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left Content */}
            <div className="lg:col-span-7">
              <div className="w-14 h-14 rounded-full bg-[#09263f] text-white flex items-center justify-center shrink-0 mb-4 shadow-xs">
                <i className="fa-solid fa-house text-2xl"></i>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#09263f] mb-3">
                {t.commercialTitle}
              </h2>
              <p className="text-gray-700 text-base sm:text-lg leading-relaxed max-w-2xl">
                {t.commercialLead}
              </p>
              <div className="mt-6 flex flex-wrap gap-4 items-center">
                <a
                  href={`tel:${companyInfo.phoneRaw}`}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#d11218] hover:bg-[#b50e14] text-white font-semibold text-sm sm:text-base rounded-md shadow-xs transition-colors"
                >
                  <i className="fa-solid fa-phone text-xs"></i>
                  <span>{t.phoneCta}</span>
                </a>
                <Link
                  to={isEn ? '/en/contact' : '/kontakt'}
                  className="inline-flex items-center gap-2 px-5 py-3 border border-gray-300 hover:border-[#09263f] bg-white text-[#09263f] font-semibold text-sm sm:text-base rounded-md transition-colors"
                >
                  <i className="fa-solid fa-envelope text-xs"></i>
                  <span>{t.contactCta}</span>
                </Link>
              </div>
            </div>

            {/* Right Image */}
            <div className="lg:col-span-5">
              <div className="overflow-hidden rounded-xl shadow-md border border-gray-200 aspect-[16/11] sm:aspect-[4/3] bg-gray-50 group">
                <img
                  src={imageUrl('/_assets/bad-vaegmonteret-toilet.jpg', 'width=800,quality=85,format=auto')}
                  alt={isEn ? 'Modern wall-hung toilet and designer tiles' : 'Væghængt toilet og elegante klinker'}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values & Trust Section */}
      <section className="bg-slate-50 py-12 sm:py-16 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#09263f]">
              {t.valuesTitle}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.values.map((v, i) => (
              <div
                key={i}
                className="bg-white rounded-lg p-6 border border-gray-200/80 shadow-xs hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 rounded-lg bg-red-50 text-[#d11218] flex items-center justify-center mb-4">
                  <i className={`${v.icon} text-xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-[#09263f] mb-2">{v.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Accordion Section */}
      <section className="bg-white py-12 sm:py-16 border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#09263f] mb-8 text-center">
            {t.faqTitle}
          </h2>
          <div className="space-y-4">
            {t.faqs.map((faq, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-xs"
              >
                <button
                  type="button"
                  onClick={() => toggleFaq(idx)}
                  className="w-full px-6 py-4 text-left font-bold text-[#09263f] hover:bg-gray-50 flex items-center justify-between transition-colors cursor-pointer"
                >
                  <span className="text-base sm:text-lg pr-4">{faq.q}</span>
                  <i
                    className={`fa-solid fa-chevron-down text-gray-400 text-sm transition-transform duration-200 shrink-0 ${
                      openFaq === idx ? 'rotate-180 text-[#d11218]' : ''
                    }`}
                  ></i>
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-5 pt-1 text-gray-600 text-sm sm:text-base leading-relaxed border-t border-gray-100 bg-gray-50/50">
                    <p>{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA Banner matching Varmepumper & VVS setup with 0 opacity to right */}
      <section
        className="relative text-white py-16 md:py-20 bg-cover bg-center overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(90deg, #06243d 0%, rgba(6, 36, 61, 0.95) 35%, rgba(6, 36, 61, 0.4) 55%, rgba(6, 36, 61, 0) 80%), url("${imageUrl('/_assets/solrig-industriga-rd-med-ra-de-porte-og-varevogne.png', 'width=1600,quality=85,format=auto')}")`,
          backgroundPosition: 'center center',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-[520px]">
            <h2 className="text-[clamp(26px,3vw,34px)] font-[800] text-white tracking-[-0.01em] leading-[1.2] mb-3">
              {isEn ? 'Ready for your dream bathroom?' : 'Klar til et nyt badeværelse?'}
            </h2>
            <p className="text-[clamp(14px,1.5vw,16px)] text-white/90 leading-[1.6] mb-8 font-normal">
              {isEn
                ? 'Contact us for a non-binding inspection and quote. We help you all the way from planning to finished installation.'
                : 'Kontakt os for en uforpligtende besigtigelse og et fast tilbud. Vi hjælper dig hele vejen fra idé og opmåling til færdigt badeværelse.'}
            </p>
            <div className="flex flex-wrap items-center gap-3.5">
              <Link
                to={isEn ? '/en/get-a-quote' : '/tilbud'}
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#df0011] hover:bg-[#b8000e] text-white font-bold text-sm tracking-wide rounded-md transition-all shadow-md group"
              >
                <span>{t.ctaButton}</span>
                <i className="fa-solid fa-arrow-right text-xs"></i>
              </Link>
              <a
                href={`tel:${companyInfo.phoneRaw}`}
                className="inline-flex items-center gap-2 px-6 py-3.5 border border-white/40 hover:bg-white/10 text-white font-bold text-sm rounded-md transition-all"
              >
                <i className="fa-solid fa-phone text-xs"></i>
                <span>{companyInfo.phone}</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
